// src/components/InternalSentimentView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DetailedListView from './DetailedListView';
import { departmentQueries } from '../repository/graphQueries';
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay';

const communicationColumns = [
    { header: 'Date', key: 'date', formatter: formatDateTime},
    { header: 'Type', key: 'type'},
    { header: 'Title', key: 'title'},
    { header: 'Staff', key: 'staff'},
    { header: 'Call Scores', key: 'callEvaluations', width: '50%', formatter: (evals) => (evals && evals.length > 0) ? <CallEvalDisplay evaluations={evals} /> : <span className="text-gray-400 italic text-xs">No Evals</span> },
    { header: 'Summary', key: 'summary', type: 'longText'},
    { header: 'Evidenced Entities', key: 'evidencedEntities', formatter: (val) => val?.map(e => `${e.name} (${e.type})`).join(', ') },
];

const InternalSentimentView = () => {
    const { neo4jService, departmentId: departmentIdFromContext, dateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [communicationsList, setCommunicationsList] = useState([]);
    const [loadingComms, setLoadingComms] = useState(false);
    const [errorComms, setErrorComms] = useState(null);
    const [sentimentTrend, setSentimentTrend] = useState([]);
    const [loadingTrend, setLoadingTrend] = useState(false);

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!neo4jService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [neo4jService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (event) => {
        const newDept = event.target.value;
        navigate(`/internal/${newDept}/sentiment`);
    };

    const loadDepartmentComms = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingComms(true); setErrorComms(null);
        try {
            const params = { 
                departmentId: selectedDepartment,
                startDate: dateRange.start,
                endDate: dateRange.end
            };
            const comms = await neo4jService.runQuery(departmentQueries.getDepartmentCommunicationsList, params);
            setCommunicationsList(comms);
        } catch (e) { setErrorComms(e.message); setCommunicationsList([]); }
        finally { setLoadingComms(false); }
    }, [neo4jService, selectedDepartment, dateRange]);

    const loadDepartmentSentimentTrend = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingTrend(true);
        try {
            const params = { 
                departmentId: selectedDepartment,
                startDate: dateRange.start,
                endDate: dateRange.end
            };
            const trendData = await neo4jService.runQuery(departmentQueries.getDepartmentSentimentOverTime, params);
            setSentimentTrend(trendData?.map(item => ({
                ...item,
                weekBeginning: formatDate(item.weekBeginning) 
          })) ?? []);
        } catch (err) { console.error("Error loading dept sentiment trend:", err); setSentimentTrend([]); }
        finally { setLoadingTrend(false); }
    }, [neo4jService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentComms();
            loadDepartmentSentimentTrend();
        }
    }, [selectedDepartment, loadDepartmentComms, loadDepartmentSentimentTrend]);

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Department Communications & Sentiment</h2>
                {departments.length > 0 && (
                    <div className="flex items-center space-x-2">
                        <label htmlFor="department-select-sentiment" className="text-sm font-medium text-gray-700">
                            Department:
                        </label>
                        <select
                            id="department-select-sentiment"
                            value={selectedDepartment}
                            onChange={handleDepartmentChange}
                            className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            disabled={loadingDepartments || !departments.length}
                        >
                            {departments.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Department Sentiment Trend (Weekly)</h3>
                 {loadingTrend ? <p>Loading chart...</p> : sentimentTrend.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentTrend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="weekBeginning" tick={{fontSize: 10}} />
                            <YAxis domain={[0, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} tick={{fontSize: 10}} />
                            <Tooltip wrapperClassName="text-xs" formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Avg Sentiment']} labelFormatter={(value) => `Week: ${value}`} />
                            <Line type="monotone" dataKey="avgSentiment" stroke="#3B82F6" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} name="Avg Sentiment" />
                        </LineChart>
                      </ResponsiveContainer>
                  ) : (
                      <p className="text-center text-sm text-gray-500 py-4">No sentiment data available for this department.</p>
                  )}
             </div>

            <DetailedListView
                title="Department Communications"
                items={communicationsList}
                columns={communicationColumns}
                isLoading={loadingComms}
                error={errorComms}
            />
        </>
    );
};

export default InternalSentimentView;